import React, { useCallback, useEffect, useState } from "react";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import ConfirmDialog from "components/ConfirmDialog";
import { useTeamAdminBrand } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import { formatEuro, formatShortDate } from "./formatters";
import ManualRevenueForm from "./ManualRevenueForm";

const formatEntryDate = (ymd, dateLocale) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || "—";
  const [y, m, d] = ymd.split("-").map(Number);
  return formatShortDate(new Date(y, m - 1, d), dateLocale);
};

const ManualRevenueTab = () => {
  const { t, dateLocale, locale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/revenue-entries", {
          params: {
            ...adminBrandParams({ brand: adminBrand }),
            page,
            limit: 25,
          },
        });
        setEntries(data.entries || []);
        setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
      } catch (err) {
        setError(err?.response?.data?.message || t("revenue.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [adminBrand, t]
  );

  useEffect(() => {
    load(1);
  }, [load, reloadSignal]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/revenue-entries/${deleteTarget._id}`, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      setDeleteTarget(null);
      setReloadSignal((n) => n + 1);
    } catch (err) {
      setError(err?.response?.data?.message || t("revenue.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="team-manual team-revenue">
      <ManualRevenueForm onCreated={() => setReloadSignal((n) => n + 1)} />

      <div className="team-manual-list">
        <h2 className="team-manual-list-title">{t("revenue.listTitle")}</h2>
        <p className="team-lead">{t("revenue.listLead")}</p>

        {error && <div className="auth-alert is-error">{error}</div>}

        {loading ? (
          <div className="team-empty">{t("common.loading")}</div>
        ) : entries.length === 0 ? (
          <p className="team-muted">{t("common.noEntries")}</p>
        ) : (
          <>
            <div className="team-table-wrap">
              <table className="team-table">
                <thead>
                  <tr>
                    <th>{t("revenue.colDate")}</th>
                    <th>{t("revenue.colAmount")}</th>
                    <th>{t("revenue.colType")}</th>
                    <th>{t("revenue.colCreated")}</th>
                    <th aria-label={t("revenue.colActions")} />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry._id}>
                      <td>{formatEntryDate(entry.entryDate, dateLocale)}</td>
                      <td>{formatEuro(entry.priceCents, locale)}</td>
                      <td>{entry.massageType || "—"}</td>
                      <td>{formatShortDate(entry.createdAt, dateLocale)}</td>
                      <td className="team-table-actions">
                        <button
                          type="button"
                          className="team-delete-btn"
                          onClick={() => setDeleteTarget(entry)}
                        >
                          {t("revenue.delete")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="team-pagination">
                <button
                  type="button"
                  disabled={pagination.page <= 1}
                  onClick={() => load(pagination.page - 1)}
                >
                  {t("common.back")}
                </button>
                <span>
                  {pagination.page} / {pagination.pages}
                </span>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => load(pagination.page + 1)}
                >
                  {t("common.next")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t("revenue.deleteTitle")}
        message={t("revenue.deleteConfirm", {
          amount: formatEuro(deleteTarget?.priceCents, locale),
          date: formatEntryDate(deleteTarget?.entryDate, dateLocale),
        })}
        confirmLabel={t("revenue.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        busy={deleting}
      />
    </div>
  );
};

export default ManualRevenueTab;
